import { Alert, Button, Checkbox, Divider, Modal, Select, Stack, Text } from "@mantine/core";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { addShiftEnrolment } from "../../../../data/actions/shiftActions";
import { IState } from "../../../../data/reducers/state";
import { Role } from "../../../../types/persons/Role";
import { IPerson } from "../../../../types/persons/person";
import { Privilege } from "../../../../types/persons/privilege";
import { IShift } from "../../../../types/shifts/Shift";
import { IShiftEnrolment } from "../../../../types/shifts/ShiftEnrolment";
import { ShiftEnrolmentType } from "../../../../types/shifts/enums/ShiftEnrolmentType";
import PersonSearcher from "../../../common/person/PersonSearcher";
import ShiftPersonsEditor from "../ShiftPersonsEditor";
import { RoleIconSelector } from "./roleIcons";

// import { Stack, Text, TextInput } from "@mantine/core";

interface ShiftPersonsEditorProps {
  isNew: boolean;
  shift: IShift;

  actions: {
    addShiftEnrolment: (enrolment: IShiftEnrolment) => Promise<IShiftEnrolment>;
  };
  onEdit?: (name: string, value: any) => void;
}

const ShiftPersonsManager: React.FunctionComponent<ShiftPersonsEditorProps> = ({
  shift,
  actions,
  onEdit,
}) => {
  const [highlightedPerson, setPersonToScrollTo] = React.useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [selectedPerson, setSelectedPerson] = React.useState<IPerson | null>(null);
  const [draftEnrolment, setDraftEnrolment] = React.useState<Partial<IShiftEnrolment> | null>(null);
  const personsToIgnore = React.useMemo(
    () => new Set(shift?.enrolments?.map((p) => p.person) || []),
    [shift?.enrolments],
  );
  const enrolmentTypeOptions = React.useMemo(
    () =>
      ShiftEnrolmentType.all().map((type) => ({
        value: type.toString(),
        label: ShiftEnrolmentType.toString(type),
      })),
    [],
  );

  const launchAddModal = () => {
    setSelectedPerson(null);
    setDraftEnrolment({
      shift: shift._id,
      role: Role.referee,
      privilege: Privilege.none,
      type: ShiftEnrolmentType.casual,
    });
    setAddModalOpen(true);
  };

  const handlePersonSelected = (person: IPerson) => {
    setSelectedPerson(person);
    setDraftEnrolment((prev) => ({
      ...(prev || {}),
      person: person._id,
    }));
  };

  const toggleDraftRole = (role: Role, checked: boolean) => {
    if (!draftEnrolment) return;
    const newRole = checked ? Role.addRole(draftEnrolment.role || Role.none, role) : Role.removeRole(draftEnrolment.role || Role.none, role);
    setDraftEnrolment({ ...draftEnrolment, role: newRole });
  };

  const handleTypeChange = (value: string | null) => {
    if (!draftEnrolment || value == null) return;
    setDraftEnrolment({ ...draftEnrolment, type: parseInt(value) });
  };

  const handlePrivilegeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!draftEnrolment) return;
    setDraftEnrolment({
      ...draftEnrolment,
      privilege: event.currentTarget.checked ? Privilege.shiftSupervisor : Privilege.none,
    });
  };

  const handleAddSubmit = () => {
    if (!draftEnrolment?.person) return;
    const payload: IShiftEnrolment = {
      person: draftEnrolment.person,
      shift: shift._id,
      role: draftEnrolment.role || Role.referee,
      privilege: draftEnrolment.privilege ?? Privilege.none,
      type: draftEnrolment.type ?? ShiftEnrolmentType.casual,
    };
    actions
      .addShiftEnrolment(payload)
      .then(() => {
        setAddModalOpen(false);
        setSelectedPerson(null);
        setDraftEnrolment(null);
        setPersonToScrollTo(payload.person.toString());
      })
      .catch((er) => alert(er.message));
  };

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Alert color="yellow" variant="light" radius="md" style={{ marginBottom: "10px" }}>
          When enabled, permanent officials can view this shift and will appear
          in the available list without being manually enrolled.
        </Alert>
        <Checkbox
          checked={!!shift.includeAssociationPermanentReferees}
          label="Include all permanent association officials"
          description="Automatically add permanent association members to this shift"
          onChange={(event) =>
            onEdit?.(
              "includeAssociationPermanentReferees",
              event.currentTarget.checked,
            )
          }
        />

      </Stack>
      <Divider my="xs" label="Shift enrolments" labelPosition="center" variant="dashed" />
      <Button
        variant="filled"
        color="teal"
        style={{ alignSelf: "flex-end", minWidth: 160 }}
        onClick={launchAddModal}
      >
        Add Person
      </Button>
      <ShiftPersonsEditor
        shiftId={shift._id}
        personToScrollTo={highlightedPerson}
      />
      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add shift enrolment"
        centered
        size="lg"
        styles={{ body: { minHeight: 320 } }}
      >
        <Stack gap="sm">
          <Text component="label" size="sm" c="dimmed">
            Search for a person to enrol
          </Text>
          <PersonSearcher
            associationId={shift.associationId}
            personsToIgnore={personsToIgnore}
            onChange={handlePersonSelected}
          />
          {draftEnrolment && (
            <Stack gap="sm">
              <div>
                Assigning to:{" "}
                {selectedPerson ? <strong>{selectedPerson.fullName}</strong> : <em>Select a person</em>}
              </div>
              <div>
                <Text component="label" size="sm" fw={600}>
                  Enrolment type
                </Text>
                <Select
                  aria-label="Enrolment type"
                  placeholder="Select enrolment type"
                  value={(draftEnrolment.type ?? ShiftEnrolmentType.casual).toString()}
                  data={enrolmentTypeOptions}
                  onChange={handleTypeChange}
                  mt={4}
                />
                <Text size="xs" c="dimmed">
                  Determines whether this official is permanent, casual, or emergency.
                </Text>
              </div>
              <RoleIconSelector value={draftEnrolment.role ?? Role.none} onToggle={toggleDraftRole} />
              <Checkbox
                label="Shift supervisor privilege"
                description="Allows this person to manage the shift roster."
                checked={(draftEnrolment.privilege ?? Privilege.none) === Privilege.shiftSupervisor}
                onChange={handlePrivilegeToggle}
              />
              <Button
                variant="filled"
                color="teal"
                onClick={handleAddSubmit}
                disabled={!draftEnrolment.person}
              >
                Add enrolment
              </Button>
            </Stack>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
};

function mapStateToProps(state: IState, ownProps) {
  return {
    personsMap: state.persons.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ addShiftEnrolment }, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ShiftPersonsManager);
